import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoader } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { DataTable } from "@/components/common/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2, Plus, Users, MapPin, ChevronRight, Layers } from "lucide-react";
import { toast } from "sonner";

export default function Agencies() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [isMaster, setIsMaster] = useState(false);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(async () => {
    try {
      const [b, t, d] = await Promise.all([api.get("/businesses"), api.get("/business-types"), api.get("/agency-districts")]);
      setItems(b.data.items); setTypes(t.data.items);
      setDistricts(d.data.items); setIsMaster(d.data.is_master_admin);
    } catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!items) return <PageLoader />;

  const columns = [
    { key: "name", header: "Business Name", render: (r) => <span className="font-medium text-slate-800">{r.name}</span> },
    { key: "type", header: "Type", render: (r) => r.business_type || "—" },
    { key: "loc", header: "Location", render: (r) => [r.city, r.state].filter(Boolean).join(", ") || "—" },
    { key: "phone", header: "Phone", render: (r) => r.phone || "—" },
    { key: "status", header: "Status", render: (r) => r.status || "active" },
  ];
  return (
    <div>
      <PageHeader title="Agencies / Businesses" description="External business entities related to People, scoped to your selected District (top bar)."
        actions={<Button className="bg-brand hover:bg-brand-strong" data-testid="create-business-button" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1.5" /> New Agency / Business</Button>} />

      <section>
        {items.length ? <DataTable columns={columns} rows={items} onRowClick={(r) => navigate(`/agencies/${r.id}`)} testId="businesses-table" />
          : <EmptyState icon={Building2} title="No agencies in this District" description="No Agency / Business records for the selected District. Switch the District in the top bar or create one." />}
      </section>

      {open && <CreateBusiness types={types} onClose={() => setOpen(false)} onCreated={(b) => { setOpen(false); navigate(`/agencies/${b.id}`); }} />}
    </div>
  );
}

function CreateBusiness({ types, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", business_type: "", city: "", state: "", phone: "", email: "", website: "" });
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!f.name.trim()) return;
    setSaving(true);
    try { const { data } = await api.post("/businesses", f); if (data.duplicate_warning) toast.warning(data.duplicate_warning); else toast.success("Business created"); onCreated(data); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="create-business-dialog">
        <DialogHeader><DialogTitle>New Agency / Business</DialogTitle><DialogDescription>Duplicate names are flagged for review.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs font-semibold text-slate-600">Business Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1" data-testid="business-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-slate-600">Type</Label>
              <Select value={f.business_type} onValueChange={(v) => setF({ ...f, business_type: v })}><SelectTrigger className="mt-1" data-testid="business-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">{types.map((t) => <SelectItem key={t.id} value={t.label}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs font-semibold text-slate-600">Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">City</Label><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs font-semibold text-slate-600">State</Label><Input value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className="mt-1" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="business-submit" className="bg-brand hover:bg-brand-strong">Create</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
